import { z } from 'zod';
import { EnvelopeSchema, IdSchema } from './envelope.js';
/**
 * WebSocket frames. Both sides import these schemas, so the client and the
 * server cannot drift: the schema is the contract.
 */
// ---- client -> server ----
export const ClientFrameSchema = z.discriminatedUnion('t', [
    /**
     * First frame after connect, required within a few seconds or the server
     * closes the socket. `accessToken` authenticates the connection — a
     * browser WebSocket handshake can't set an Authorization header, so the
     * token travels in-band instead of in the URL (which would leak into
     * server access logs). `since` is the last message id the client holds,
     * so the server knows what to drain from the offline queue.
     */
    z.object({ t: z.literal('hello'), accessToken: z.string(), since: IdSchema.optional() }),
    z.object({ t: z.literal('send'), envelope: EnvelopeSchema }),
    z.object({
        t: z.literal('receipt'),
        messageIds: z.array(IdSchema).max(200),
        kind: z.enum(['delivered', 'read']),
    }),
    z.object({ t: z.literal('typing'), convId: IdSchema, on: z.boolean() }),
    z.object({ t: z.literal('ping') }),
]);
// ---- server -> client ----
export const ServerFrameSchema = z.discriminatedUnion('t', [
    z.object({ t: z.literal('ready'), serverTime: z.number().int(), userId: IdSchema }),
    z.object({ t: z.literal('message'), envelope: EnvelopeSchema }),
    z.object({ t: z.literal('ack'), id: IdSchema, ts: z.number().int() }),
    z.object({
        t: z.literal('receipt'),
        messageId: IdSchema,
        userId: IdSchema,
        kind: z.enum(['delivered', 'read']),
        at: z.number().int(),
    }),
    z.object({ t: z.literal('typing'), convId: IdSchema, userId: IdSchema, on: z.boolean() }),
    z.object({ t: z.literal('presence'), userId: IdSchema, online: z.boolean() }),
    /** Backlog drained; the client can stop showing "catching up". */
    z.object({ t: z.literal('synced'), count: z.number().int().nonnegative() }),
    z.object({ t: z.literal('pong') }),
    /**
     * `reqId` is surfaced in the UI on failure so it can be grepped out of the
     * server log. See docs/debugging.md.
     */
    z.object({
        t: z.literal('error'),
        code: z.string(),
        message: z.string(),
        reqId: z.string(),
        fatal: z.boolean().default(false),
    }),
]);
//# sourceMappingURL=ws.js.map